import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { api } from '../lib/api';
import type { Notification } from '../shared/types';

/**
 * Unread count in the header. Polls every 60s — in-app notifications are the only
 * channel (no SMS, no email, no push), so this is how anyone finds out about
 * anything. The API already filters by audience, so a child never counts an
 * adults-only notification.
 *
 * A failed poll leaves the last known count rather than flashing zero — showing
 * "all clear" because a fetch failed is exactly the lie the hub review caught.
 */
export default function NotificationBell() {
  const [unread, setUnread] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const rows = await api.get<Notification[]>('/notifications');
        if (!cancelled) setUnread(rows.filter((n) => !n.read_at).length);
      } catch {
        // keep the last known count; don't claim zero
      }
    };

    poll();
    const id = setInterval(poll, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <NavLink
      to="/notifications"
      className="relative rounded-full p-2 text-ink-soft transition-colors hover:bg-surface-sunk hover:text-ink"
      aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      {unread ? (
        <span
          className="absolute top-0.5 right-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-clay px-1 text-[10px] font-semibold text-white"
          aria-hidden="true"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      ) : null}
    </NavLink>
  );
}
